<template>
    <div class="rounded-lgr border bg-surface-sunken p-4" :class="section.parent_ref ? 'border-border' : 'border-border-strong'">
        <div class="flex items-start gap-2">
            <button type="button" class="section-handle cursor-grab text-ink-muted hover:text-ink pt-2" aria-label="Drag to reorder section">
                <i class="bi bi-grip-vertical" aria-hidden="true"></i>
            </button>

            <div class="grow flex flex-col gap-2">
                <div class="flex flex-wrap items-center gap-2">
                    <input
                        :value="section.title"
                        type="text"
                        maxlength="255"
                        :placeholder="section.parent_ref ? 'Sub-section title' : 'Section title'"
                        aria-label="Section title"
                        class="grow px-3 py-2 border border-control-border rounded-lg focus-field body-small-bold bg-white"
                        @input="update({ title: ($event.target as HTMLInputElement).value })" />

                    <label :for="`${uid}-code`" class="flex items-center gap-2 details text-ink-muted shrink-0">
                        <span title="Stable identifier used by reports and scoring. Unique within this survey.">Code</span>
                        <input
                            :id="`${uid}-code`"
                            :value="section.code"
                            type="text"
                            maxlength="64"
                            placeholder="e.g. leading_others"
                            class="w-40 px-2 py-2 border border-control-border rounded-lg focus-field details bg-white"
                            @input="update({ code: ($event.target as HTMLInputElement).value })" />
                    </label>
                    <label :for="`${uid}-enabled`" class="flex items-center gap-2 details text-ink-muted">
                        <input :id="`${uid}-enabled`" :checked="section.enabled" type="checkbox" @change="update({ enabled: ($event.target as HTMLInputElement).checked })" />
                        Enabled
                    </label>
                    <button type="button" class="details text-red-600 hover:text-red-700 px-2 py-1" @click="builder.removeSection(section.ref)">
                        <i class="bi bi-trash" aria-hidden="true"></i> Delete
                    </button>
                </div>

                <textarea
                    :value="section.instructions"
                    rows="2"
                    placeholder="Instructions shown on the section intro card (optional)"
                    aria-label="Section instructions"
                    class="w-full px-3 py-2 border border-control-border rounded-lg details bg-white focus-field"
                    @input="update({ instructions: ($event.target as HTMLTextAreaElement).value })"></textarea>
                <textarea
                    v-if="!section.parent_ref"
                    :value="section.report_section_description"
                    rows="2"
                    placeholder="Report-only text shown under this section's heading in the completed report. Markdown supported; participants never see it (optional)"
                    aria-label="Report section description"
                    class="w-full px-3 py-2 border border-control-border rounded-lg details bg-white focus-field"
                    @input="update({ report_section_description: ($event.target as HTMLTextAreaElement).value })"></textarea>
            </div>
        </div>

        <div class="mt-3 pl-6 flex flex-col gap-3">
            <div v-if="childSections.length === 0" ref="questionList" :data-list-id="section.ref" class="flex flex-col gap-2">
                <SurveyQuestionRow
                    v-for="(question, index) in questions"
                    :key="question.client_id"
                    :question="question"
                    :index="index"
                    :question-types="choices.question_types"
                    :scales="choices.scales"
                    :strength-directions="choices.strength_directions"
                    :default-scale-label="defaultScaleLabel"
                    :locked="builder.hasResponses(question.id)"
                    @focus="$emit('focusQuestion', question.client_id)"
                    @update="builder.updateQuestion(question.client_id, $event)"
                    @duplicate="builder.duplicateQuestion(question.client_id)"
                    @remove="builder.removeQuestion(question.client_id)" />
            </div>
            <p v-else-if="questions.length > 0" class="details text-red-600">This section has sub-sections, so its own questions must be moved into one before saving.</p>

            <div v-if="!section.parent_ref" ref="childList" :data-list-id="section.ref" class="flex flex-col gap-3">
                <SurveySectionCard
                    v-for="child in childSections"
                    :key="child.ref"
                    :section="child"
                    :builder="builder"
                    :choices="choices"
                    :default-scale-label="defaultScaleLabel"
                    @focus-question="$emit('focusQuestion', $event)" />
            </div>

            <div class="flex items-center gap-3">
                <button v-if="childSections.length === 0" type="button" class="details text-button-tertiary-text hover:underline" @click="onAddQuestion">
                    <i class="bi bi-plus-lg" aria-hidden="true"></i> Add question
                </button>
                <button v-if="!section.parent_ref" type="button" class="details text-button-tertiary-text hover:underline" @click="builder.addSection(section.ref)">
                    <i class="bi bi-plus-lg" aria-hidden="true"></i> Add sub-section
                </button>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { useSortableList } from "~vue/composables/useSortableList";
import SurveyQuestionRow from "~vue/SurveyBuilder/SurveyQuestionRow.vue";
import type { BuilderChoices, BuilderSection } from "~vue/SurveyBuilder/types";
import type { SurveyBuilder } from "~vue/SurveyBuilder/useSurveyBuilder";
import { computed, useId, useTemplateRef } from "vue";

const props = defineProps<{
    section: BuilderSection;
    builder: SurveyBuilder;
    choices: BuilderChoices;
    defaultScaleLabel: string;
}>();

const emit = defineEmits<{ focusQuestion: [clientId: string] }>();

const uid = useId();
const questionList = useTemplateRef<HTMLElement>("questionList");
const childList = useTemplateRef<HTMLElement>("childList");
const questions = computed(() => props.builder.questionsOf(props.section.ref));
const childSections = computed(() => props.builder.childSectionsOf(props.section.ref));

useSortableList(questionList, { group: "survey-questions", handle: ".question-handle", onMove: props.builder.applyQuestionMove });
// Sub-sections drag between parents but never out to the top level, which
// would nest a section two deep.
useSortableList(childList, { group: "survey-subsections", handle: ".section-handle", onMove: props.builder.applySectionMove });

function update(changes: Partial<BuilderSection>) {
    props.builder.updateSection(props.section.ref, changes);
}

function onAddQuestion() {
    const question = props.builder.addQuestion(props.section.ref);
    if (question) {
        emit("focusQuestion", question.client_id);
    }
}
</script>
